import { localizedStorefrontHref } from "../storefrontRouting";
import type { PageLink, ShopTemplateBlock } from "../types";

export const MORROW_FOOTER_PAGE_TEMPLATE_KEYS = {
    cookies: "morrow.cookies",
    delivery: "morrow.delivery",
    giftCards: "morrow.giftCards",
    privacy: "morrow.privacy",
    returns: "morrow.returns",
    sustainability: "morrow.sustainability",
    terms: "morrow.terms",
} as const;

export type MorrowFooterPageTemplateKey = typeof MORROW_FOOTER_PAGE_TEMPLATE_KEYS[keyof typeof MORROW_FOOTER_PAGE_TEMPLATE_KEYS];

const footerMessageTemplateKeys: Readonly<Record<string, MorrowFooterPageTemplateKey>> = {
    "template.morrow.footer.cookies": MORROW_FOOTER_PAGE_TEMPLATE_KEYS.cookies,
    "template.morrow.footer.delivery": MORROW_FOOTER_PAGE_TEMPLATE_KEYS.delivery,
    "template.morrow.footer.giftCards": MORROW_FOOTER_PAGE_TEMPLATE_KEYS.giftCards,
    "template.morrow.footer.privacy": MORROW_FOOTER_PAGE_TEMPLATE_KEYS.privacy,
    "template.morrow.footer.returns": MORROW_FOOTER_PAGE_TEMPLATE_KEYS.returns,
    "template.morrow.footer.sustainability": MORROW_FOOTER_PAGE_TEMPLATE_KEYS.sustainability,
    "template.morrow.footer.terms": MORROW_FOOTER_PAGE_TEMPLATE_KEYS.terms,
};

const footerTemplateKeySet = new Set<MorrowFooterPageTemplateKey>(Object.values(MORROW_FOOTER_PAGE_TEMPLATE_KEYS));

function normalizedLanguageCode(languageCode: string | null | undefined): string {
    return languageCode?.trim().toLowerCase().replace("_", "-").split("-", 1)[0] ?? "";
}

function isMorrowFooterPageTemplateKey(value: unknown): value is MorrowFooterPageTemplateKey {
    return typeof value === "string" && footerTemplateKeySet.has(value as MorrowFooterPageTemplateKey);
}

export function morrowFooterPageTemplateKey(block: ShopTemplateBlock): MorrowFooterPageTemplateKey | null {
    const explicitTemplateKey = block.options?.morrowPageTemplateKey;

    if (isMorrowFooterPageTemplateKey(explicitTemplateKey)) {
        return explicitTemplateKey;
    }

    return footerMessageTemplateKeys[block.defaultMessageKeys?.["content.text"] ?? ""] ?? null;
}

export function morrowFooterPageHref(
    block: ShopTemplateBlock,
    pages: PageLink[],
    languageCode: string | null | undefined,
): string | null {
    const templateKey = morrowFooterPageTemplateKey(block);

    if (templateKey === null) {
        return null;
    }

    const candidates = pages.filter((page) => page.templateKey === templateKey);
    const requestedLanguage = normalizedLanguageCode(languageCode);
    const page = requestedLanguage === ""
        ? candidates[0]
        : candidates.find((candidate) => (
            normalizedLanguageCode(candidate.languageCode) === requestedLanguage
        )) ?? candidates.find((candidate) => normalizedLanguageCode(candidate.languageCode) === "");

    if (!page) {
        return null;
    }

    const slug = page.slug.trim();
    const href = page.href?.trim()
        || (slug === "" ? "" : slug.startsWith("/") ? slug : `/${slug}`);

    return href === "" ? null : localizedStorefrontHref(href, languageCode);
}
